import posthog from 'posthog-js';

import { beforeSend } from './beforeSend';
import { Tracker } from './Tracker';

declare global {
  interface Window {
    EncTracking?: {
      identify?: (params: {
        email?: string;
        userId?: string;
        firstName?: string;
        lastName?: string;
        language?: string;
      }) => void;
      clearCookies?: () => void;
    };
  }
}

export class FrontendTracker implements Tracker {
  private enabled: boolean;

  constructor() {
    this.enabled =
      Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY) && !process.env.NEXT_PUBLIC_DISABLE_POSTHOG;
  }

  init() {
    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;

    if (!this.enabled || !apiKey) {
      console.info('FrontendTracker disabled (missing NEXT_PUBLIC_POSTHOG_KEY)');
      return;
    }

    // Check if PostHog is already initialized by instrumentation-client.ts
    if (!posthog.__loaded) {
      posthog.init(apiKey, {
        api_host: '/ingest', // Use same API host as instrumentation-client
        ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.posthog.com',
        persistence: 'localStorage+cookie',
        person_profiles: 'always',
        defaults: '2025-05-24',
        // ADD: Enable error tracking (top-level property)
        capture_exceptions: true,
        // ADD: Enable tracing headers for backend error context
        __add_tracing_headers: [process.env.NEXT_PUBLIC_SITE_URL || 'localhost:3000'],
        before_send: beforeSend,
        debug: process.env.NODE_ENV === 'development',
      });
    } else if (process.env.NODE_ENV === 'development') {
      console.info('PostHog already initialized by instrumentation-client');
    }
  }

  // ADD: Error capture method
  captureException(error: Error, properties?: Record<string, any>): void {
    if (!this.enabled) {
      return;
    }

    if (posthog.__loaded) {
      posthog.captureException(error, properties);
    }
  }

  async identify(traits?: Record<string, any>, _anonymousId?: string) {
    if (!this.enabled) {
      return;
    }

    if (posthog.__loaded) {
      posthog.identify(traits?.id, {
        email: traits?.email,
        firstName: traits?.firstName || '',
        lastName: traits?.lastName || '',
        language: traits?.language || '',
        role: traits?.role || '',
        roles: traits?.roles || [],
        tenantId: traits?.tenantId || '',
        companyId: traits?.companyId || '',
      });
    }
  }
  async reset() {
    if (!this.enabled) {
      return;
    }

    if (typeof window !== 'undefined') {
      window.EncTracking = window.EncTracking || {};
      window.EncTracking.clearCookies?.();
    }

    if (posthog.__loaded) {
      posthog.reset(true);
    }
  }
}
